import React, { useContext, useState } from 'react';
import { AdUrlPanel } from './AdUrlPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Link2 } from 'lucide-react';
import { axios, auth, mbUrl, TEST_LABEL, INVITE_LABEL, MbContext, fmtTotals, cpsClass, muted, primary, runCall, toast, copyText } from './mbApi';
import { ArchiveToggle } from './ArchiveToggle';

const InviteLink = ({ ibId, t, canWrite, canManage, reload }) => {
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState('');
  const create = () => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/tests/${t.id}/invite-link`), {}, auth());
    reload();
  }, 'Invite link ready').then((r) => { if (!r) reload(); });
  const resolve = (link) => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/tests/${t.id}/invite-link/resolve`), { invite_link: link }, auth());
    reload();
  }, 'Status link updated');
  const st = t.invite_status || 'tiada';

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`mb-invite-${t.id}`}>
      <Link2 size={11} />
      <span data-testid={`mb-invite-status-${t.id}`} className={st === 'berjaya' ? 'text-emerald-400' : st === 'gagal' ? 'text-red-400' : st === 'tidak_pasti' ? 'text-amber-400' : ''}>
        {INVITE_LABEL[st]}{t.invite_source === 'mock' ? ' (mock)' : ''}
      </span>
      {st === 'berjaya' && (
        <button className="underline truncate max-w-[220px]" data-testid={`mb-invite-copy-${t.id}`}
          onClick={async () => { if (await copyText(t.invite_link)) toast.success('Link disalin'); else toast.error('Gagal salin — pilih teks link dan salin manual'); }}>
          {t.invite_link} <Copy size={10} className="inline" />
        </button>
      )}
      {canWrite && (st === 'tiada' || st === 'gagal') && (
        <Button size="sm" variant="outline" className="h-9 text-[11px]" disabled={busy} onClick={create} data-testid={`mb-invite-create-${t.id}`}>
          {st === 'gagal' ? 'Cuba lagi' : 'Generate Invite Link'}
        </Button>
      )}
      {st === 'tidak_pasti' && canManage && (
        <>
          <Input className="h-9 text-[11px] w-44" placeholder="https://t.me/+… (kalau wujud)" value={manual}
            onChange={(e) => setManual(e.target.value)} data-testid={`mb-invite-manual-${t.id}`} />
          <Button size="sm" variant="outline" className="h-9 text-[11px]" disabled={busy || !manual.trim()} onClick={() => resolve(manual.trim())} data-testid={`mb-invite-resolve-link-${t.id}`}>Simpan link</Button>
          <Button size="sm" variant="outline" className="h-9 text-[11px]" disabled={busy} onClick={() => resolve('')} data-testid={`mb-invite-resolve-fail-${t.id}`}>Tanda gagal</Button>
        </>
      )}
      {t.invite_message && st !== 'berjaya' && <span className={muted}>· {t.invite_message}</span>}
    </div>
  );
};

const AdIdEditor = ({ ibId, t, reload }) => {
  const { accounts } = useContext(MbContext);
  const [adId, setAdId] = useState(t.ad_id || '');
  const [acc, setAcc] = useState(t.ad_account_id || (accounts.length === 1 ? accounts[0] : ''));
  const [busy, setBusy] = useState(false);
  const save = () => runCall(setBusy, async () => {
    await axios.put(mbUrl(ibId, `/tests/${t.id}`), { ad_id: adId.trim(), ad_account_id: acc }, auth());
    reload();
  }, 'Ad ID saved');
  const unchanged = adId.trim() === (t.ad_id || '') && acc === (t.ad_account_id || '');
  return (
    <span className="flex flex-wrap items-center gap-1">
      {accounts.length > 1 ? (
        <Select value={acc || 'none'} onValueChange={(v) => setAcc(v === 'none' ? '' : v)}>
          <SelectTrigger className="h-9 w-40 text-[11px]" data-testid={`mb-test-account-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Pilih ad account —</SelectItem>
            {accounts.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <span className="font-mono" data-testid={`mb-test-account-${t.id}`}>{acc || 'Tiada ad account'}</span>
      )}
      <Input className="h-9 text-[11px] w-36" placeholder="Meta Ad ID" value={adId} onChange={(e) => setAdId(e.target.value)} data-testid={`mb-test-adid-${t.id}`} />
      <Button size="sm" variant="outline" className="h-9 text-[11px]" disabled={busy || unchanged || (adId.trim() && !acc)} onClick={save} data-testid={`mb-test-adid-save-${t.id}`}>Match</Button>
    </span>
  );
};

const META_STATUS = {
  ACTIVE: 'Aktif', IN_PROCESS: 'Dalam review', PENDING_REVIEW: 'Dalam review', PREAPPROVED: 'Pra-lulus', DISAPPROVED: 'Ditolak Meta',
  WITH_ISSUES: 'Ada isu', PAUSED: 'Dijeda', ADSET_PAUSED: 'Ad set dijeda', CAMPAIGN_PAUSED: 'Campaign dijeda',
  PENDING_BILLING_INFO: 'Tunggu maklumat bayaran', ARCHIVED: 'Diarkib di Meta', DELETED: 'Dipadam di Meta',
};

const Badge3 = ({ t, s3, meta }) => (
  <span className="flex flex-wrap gap-1" data-testid={`mb-test-status3-${t.id}`}>
    {[['running_crm', 'Running di CRM'], ['active_meta', 'Aktif di Meta'], ['delivering', 'Menerima Delivery']].map(([k, label]) => (
      <span key={k} className={`px-1.5 rounded text-[10px] ${s3?.[k] ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5'}`}>{s3?.[k] ? '✓' : '✗'} {label}</span>
    ))}
    {meta && meta !== 'ACTIVE' && (
      <span className="px-1.5 rounded text-[10px] bg-amber-500/15 text-amber-400" data-testid={`mb-test-meta-status-${t.id}`}>Status Meta: {META_STATUS[meta] || meta}</span>
    )}
  </span>
);

const AutoMatchInfo = ({ ibId, t, canWrite, reload }) => {
  const use = (s) => runCall(() => {}, async () => {
    await axios.put(mbUrl(ibId, `/tests/${t.id}`), { ad_id: s.ad_id, ad_account_id: s.ad_account_id }, auth());
    reload();
  }, `Ad ID ${s.ad_id} dipadankan`);
  if (t.ad_id && t.auto_match) {
    return <span className="text-emerald-400" data-testid={`mb-test-automatch-${t.id}`}>Auto-padan ({t.auto_match.method === 'token' ? 'token URL' : 'kod dalam nama iklan'}){t.auto_match.ad_name ? ` · ${t.auto_match.ad_name}` : ''}</span>;
  }
  const live = (t.ad_suggestions || []).filter((s) => !s.superseded);
  if (t.ad_id || !live.length) return null;
  return (
    <div className="w-full space-y-1" data-testid={`mb-test-suggestions-${t.id}`}>
      <p className="text-amber-400">CRM jumpa {live.length} iklan yang mungkin sepadan — pilih yang betul:</p>
      {live.map((s) => (
        <div key={s.ad_id} className="flex flex-wrap items-center gap-2">
          <span className="font-mono">{s.ad_id}</span><span className="truncate max-w-[260px]">{s.name}</span>
          {canWrite && <Button size="sm" variant="outline" className="h-9 text-[11px]" onClick={() => use(s)} data-testid={`mb-test-suggest-use-${t.id}-${s.ad_id}`}>Guna</Button>}
        </div>
      ))}
    </div>
  );
};

export const TestRow = ({ ibId, t, result, canWrite, canManage, reload, simulate }) => {
  const setStatus = async (status) => {
    try { await axios.put(mbUrl(ibId, `/tests/${t.id}`), { status }, auth()); reload(); } catch (e) { toast.error('Gagal tukar status test'); }
  };
  const tot = result?.totals || {};
  const first = Object.values(tot)[0];
  return (
    <div className={`rounded border border-[hsl(var(--border))] p-2 space-y-1.5 text-[11px] ${muted}`} data-testid={`mb-test-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-1.5 rounded bg-white/5 font-mono">{t.test_code}</span>
        {canWrite ? <AdIdEditor key={t.ad_id || 'none'} ibId={ibId} t={t} reload={reload} /> : <span>Ad ID: {t.ad_id || 'Tidak dipadankan'}</span>}
        <AutoMatchInfo ibId={ibId} t={t} canWrite={canWrite} reload={reload} />
        {result && (
          <span className={result.sync_ready ? 'text-emerald-400' : 'text-amber-400'} data-testid={`mb-test-sync-ready-${t.id}`}>
            {result.sync_ready ? 'Layak Disegerakkan' : `Belum Lengkap: ${result.sync_note}`}
          </span>
        )}
        <Select value={t.status} onValueChange={setStatus} disabled={!canWrite}>
          <SelectTrigger className="h-9 w-24 text-[11px]" data-testid={`mb-test-status-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TEST_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {t.archived && <span className="px-1.5 rounded bg-white/10" data-testid={`mb-test-archived-${t.id}`}>Arkib</span>}
        {((canWrite && !t.archived) || (canManage && t.archived)) && (
          <ArchiveToggle ibId={ibId} kind="tests" rec={t} label={`ujian ${t.test_code}`} reload={reload} testid={`mb-test-archive-${t.id}`} />
        )}
      </div>
      <Badge3 t={t} s3={result?.status3} meta={result?.delivery} />
      {canWrite && !t.archived && <AdUrlPanel ibId={ibId} t={t} />}
      <details className="text-[11px]"><summary className="cursor-pointer select-none" data-testid={`mb-invite-tech-${t.id}`}>Maklumat teknikal: invite Telegram</summary>
        <InviteLink ibId={ibId} t={t} canWrite={canWrite} canManage={canManage} reload={reload} />
      </details>
      {t.ad_id && (
        <p data-testid={`mb-test-result-${t.id}`}>
          Amount spent: {fmtTotals(tot)} · Impressions: {first?.impressions ?? '—'} · Link clicks: {first?.link_clicks ?? '—'} · {result?.days || 0} hari data
          {result?.delivery ? ` · Delivery: ${result.delivery}` : ''}
        </p>
      )}
      <p data-testid={`mb-test-cps-${t.id}`}>
        Join: <span className={primary}>{result?.joins || 0}</span> (unik {result?.joins_first || 0} · rejoin {result?.joins_rejoin || 0})
        {' · '}CPS: <span className={cpsClass(result?.cps_myr) || primary}>{result?.cps_myr != null ? `RM ${result.cps_myr.toFixed(2)}` : 'N/A'}</span>
        {' · '}CPS unik: {result?.cps_first_myr != null ? `RM ${result.cps_first_myr.toFixed(2)}` : 'N/A'}
        {result?.cps_note ? ` (${result.cps_note})` : ''}
        {canManage && simulate && t.invite_status === 'berjaya' && (
          <button className="ml-2 underline" onClick={() => simulate(t)} data-testid={`mb-sim-join-${t.id}`}>simulate join</button>
        )}
      </p>
    </div>
  );
};
